import React from 'react';
import { useTranslation } from 'react-i18next';
import { Email } from '../useData';
import { getNonEmbeddedAttachments } from '../utils/attachments';
import { formatDateTimeForList } from '../../../config/appConfig';
import { EmailDateDisplay } from './EmailDateDisplay';
import { cn } from '../../../lib/utils';
import { DocumentTextIcon } from '../../../components/ui/Icons';
import { MailboxLocationBadge } from './MailboxLocationBadge';
import {
  WORKFLOW_STATUS_CHIP,
  WORKFLOW_STATUS_LABELS,
  assigneeDisplayName,
  assigneeInitial,
  resolvedWorkflowStatus,
} from '../utils/workflowDisplay';
import {
  emailDisplayDateForSort,
  type EmailListSortBy,
} from '../utils/emailListSortStorage';

type EmailAddressLike = string | { email?: string; address?: string; name?: string } | null | undefined;

function addrEmail(a: EmailAddressLike): string {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return a.email ?? a.address ?? '';
}

function addrName(a: EmailAddressLike): string {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return a.name || addrEmail(a) || '';
}

export interface EmailListCardTheme {
  surface: string;
  interactiveHover: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  primaryBorderAll: string;
}

export interface EmailListCardProps {
  email: Email;
  colors: EmailListCardTheme;
  isSelected: boolean;
  isBulkSelected: boolean;
  showMailboxLocation: boolean;
  showReadByCount: boolean;
  cardRef?: React.Ref<HTMLDivElement>;
  onEmailClick: (email: Email) => void;
  onToggleRead?: (email: Email) => void;
  onBulkToggleSelect?: (id: number, add: boolean) => void;
  listSortBy?: EmailListSortBy;
}

function firstAddress(raw: Email['from']): EmailAddressLike {
  if (Array.isArray(raw)) return raw[0] ?? null;
  return raw as EmailAddressLike;
}

const EmailListCard: React.FC<EmailListCardProps> = ({
  email,
  colors,
  isSelected,
  isBulkSelected,
  showMailboxLocation,
  showReadByCount,
  cardRef,
  onEmailClick,
  onToggleRead,
  onBulkToggleSelect,
  listSortBy = 'date',
}) => {
  const { t } = useTranslation('emails');
  const fromRaw = firstAddress(email.from);
  const senderName = addrName(fromRaw) || (addrEmail(fromRaw) && addrEmail(fromRaw).split('@')[0]) || 'Unknown';

  const nonEmbedded = getNonEmbeddedAttachments(email.attachments);
  const hasAttachments = nonEmbedded.length > 0;
  const hasLinkedClient = !!(email.linkedClientId || email.linkedClient);
  const hasLinkedTodo = !!(email.linkedTodoId || email.linkedTodo);
  const hasLinkedAppointment = !!(email.linkedAppointmentId || email.linkedAppointment);
  const clientTitle = email.linkedClient
    ? `Client: ${email.linkedClient.clientName || 'Linked client'}`
    : 'Linked to client';
  const todoTitle = email.linkedTodo
    ? `Todo: ${email.linkedTodo.todoDescr || email.linkedTodo.todoId}`
    : 'Linked to todo';
  const appointmentTitle = email.linkedAppointment
    ? `Appointment: ${email.linkedAppointment.clientAppName || email.linkedAppointment.clientAppId}`
    : 'Linked to appointment';

  const previewText = email.bodyText
    ? email.bodyText.substring(0, 80).replace(/\n/g, ' ').trim()
    : email.bodyHtml
      ? email.bodyHtml.replace(/<[^>]*>/g, '').substring(0, 80).trim()
      : '';

  const normalizedPriority = (email.priority || '').toLowerCase();
  const priorityLabel =
    normalizedPriority === 'high' ? 'High'
    : normalizedPriority === 'low' ? 'Low'
    : null;

  const accountLabel =
    email.emailAccount?.displayName ||
    email.emailAccount?.emailAddress ||
    (email.emailAccountId ? `Account #${email.emailAccountId}` : '');
  const folderLabel = email.folder || 'INBOX';

  const assigneeName = assigneeDisplayName(email);
  const workflowStatus = resolvedWorkflowStatus(email);
  const statusMeta = workflowStatus ? WORKFLOW_STATUS_CHIP[workflowStatus] : null;

  const hasMentionMeta = Boolean(email.latestMentionAuthor || email.latestMentionSnippet);
  const mentionTimeLabel = email.latestMentionAt
    ? formatDateTimeForList(email.latestMentionAt)
    : null;
  const displayDate =
    listSortBy === 'date' ? email.date : emailDisplayDateForSort(email, listSortBy);

  return (
    <div
      ref={cardRef}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/x-ums-email-id', String(email.id));
        if (email.partitionAt) {
          e.dataTransfer.setData('application/x-ums-email-partition-at', email.partitionAt);
        }
        e.dataTransfer.effectAllowed = 'copy';
      }}
      onClick={() => onEmailClick(email)}
      data-email-list-card
      className={cn(
        'relative border rounded px-2 py-1.5 cursor-pointer transition-colors',
        colors.surface,
        colors.interactiveHover,
        !email.isRead && 'border-l-2 border-l-blue-500 font-semibold',
        email.isRead && colors.border,
        email.isArchived && 'opacity-90 bg-gray-50/50 dark:bg-gray-800/30',
        email.isArchived && email.isRead && 'border-l-2 border-l-gray-400 dark:border-l-gray-500',
        isSelected && cn(colors.primaryBorderAll, 'shadow-sm'),
      )}
    >
      <div className="flex items-start gap-2 min-w-0">
        {onBulkToggleSelect && (
          <input
            type="checkbox"
            checked={isBulkSelected}
            onChange={(e) => {
              e.stopPropagation();
              onBulkToggleSelect(email.id, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-gray-300 flex-shrink-0 mt-0.5"
            aria-label={`Select ${email.subject || 'email'}`}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {onToggleRead && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleRead(email); }}
                className="p-0.5 rounded flex-shrink-0 hover:bg-black/5"
                title={email.isRead ? 'Mark as unread' : 'Mark as read'}
              >
                <svg
                  className={cn('w-3.5 h-3.5', email.isRead ? colors.textMuted : 'text-blue-600')}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-label={email.isRead ? 'Read' : 'Unread'}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </button>
            )}
            {!onToggleRead && !email.isRead && (
              <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0" />
            )}
            {email.hasUnreadMention && (
              <span
                className="inline-block w-1.5 h-1.5 bg-indigo-500 rounded-full flex-shrink-0"
                title="Unread mention"
                aria-label="Unread mention"
              />
            )}
            <span className={cn('text-xs truncate', !email.isRead ? cn(colors.text, 'font-semibold') : colors.textSecondary)}>
              {senderName}
            </span>
          </div>
          <div className={cn('text-xs truncate mt-0.5 flex items-center gap-1.5', colors.textSecondary)}>
            <span className="truncate">{email.subject || '(no subject)'}</span>
            {(email.threadCount ?? 0) > 1 && (
              <span className="shrink-0 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 px-1.5 py-0.5 text-[10px] font-medium">
                {email.threadCount}
              </span>
            )}
          </div>
          {hasMentionMeta && (
            <div className={cn('text-[11px] truncate mt-0.5', colors.textMuted)}>
              <span className="font-medium text-indigo-700 dark:text-indigo-300">{email.latestMentionAuthor}</span>
              {mentionTimeLabel ? <span>{` · ${mentionTimeLabel}`}</span> : null}
              {email.latestMentionSnippet ? (
                <span>{` — ${email.latestMentionSnippet}`}</span>
              ) : null}
            </div>
          )}
          {assigneeName && (
            <div className="mt-0.5 flex items-center gap-1.5 flex-wrap min-w-0">
              <span
                className={cn(
                  'inline-flex items-center gap-1 max-w-full rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
                  'bg-indigo-50 text-indigo-800 border-indigo-200',
                  'dark:bg-indigo-950/35 dark:text-indigo-200 dark:border-indigo-800',
                )}
                title={`Assigned to ${assigneeName}`}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-indigo-200 text-[9px] font-semibold text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                  {assigneeInitial(assigneeName)}
                </span>
                <span className="truncate">{assigneeName}</span>
              </span>
              {workflowStatus && statusMeta && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium shrink-0',
                    statusMeta.chipClass,
                  )}
                  title={`Status: ${WORKFLOW_STATUS_LABELS[workflowStatus]}`}
                >
                  <span className={cn('inline-block h-1.5 w-1.5 rounded-full', statusMeta.dotClass)} />
                  {WORKFLOW_STATUS_LABELS[workflowStatus]}
                </span>
              )}
            </div>
          )}
          {previewText && (
            <p className={cn('text-[11px] truncate mt-0.5', colors.textMuted)}>
              {previewText}{previewText.length >= 80 ? '...' : ''}
            </p>
          )}
          {showMailboxLocation && accountLabel && (
            <MailboxLocationBadge
              variant="compact"
              accountLabel={accountLabel}
              folderLabel={folderLabel}
              className="mt-0.5 max-w-full"
            />
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0 pl-1 max-w-[40%]">
          <EmailDateDisplay
            date={displayDate}
            compact
            showHint={false}
            className={cn('text-[11px] whitespace-nowrap', colors.textMuted)}
            mutedClassName={colors.textMuted}
          />
          {(priorityLabel || email.isStarred || hasAttachments || email.hasNote || email.hasComments || (email.commentCount ?? 0) > 0 || email.isArchived || (email.tags?.length ?? 0) > 0 || hasLinkedClient || hasLinkedTodo || hasLinkedAppointment || (showReadByCount && (email.readByCount ?? 0) > 0)) && (
            <span className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
              {showReadByCount && (email.readByCount ?? 0) > 0 && (
                <span
                  className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded-full border flex-shrink-0 whitespace-nowrap',
                    colors.border,
                    colors.textMuted,
                  )}
                  title={`Read by ${email.readByCount} user${email.readByCount === 1 ? '' : 's'}`}
                >
                  Read by {email.readByCount}
                </span>
              )}
              {priorityLabel && (
                <span
                  className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded-full border flex-shrink-0 font-semibold whitespace-nowrap',
                    normalizedPriority === 'high'
                      ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20'
                      : normalizedPriority === 'low'
                        ? 'border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-900/20'
                        : colors.border,
                  )}
                  title="Message priority"
                >
                  {priorityLabel}
                </span>
              )}
              {email.isStarred && (
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 text-yellow-500 flex-shrink-0" title="Starred">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </span>
              )}
              {hasAttachments && (
                <span className={cn('inline-flex items-center justify-center w-3.5 h-3.5 flex-shrink-0', colors.textMuted)} title={`${nonEmbedded.length} attachment(s)`}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </span>
              )}
              {(email.hasComments || (email.commentCount ?? 0) > 0) && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 font-semibold whitespace-nowrap',
                    'border-indigo-500/70 text-indigo-900 bg-indigo-100 ring-1 ring-indigo-500/25',
                    'dark:bg-indigo-950/70 dark:text-indigo-100 dark:border-indigo-400/60 dark:ring-indigo-400/20',
                  )}
                  title={t('commentsBadge')}
                >
                  <DocumentTextIcon className="w-3.5 h-3.5 shrink-0" aria-hidden />
                  {(email.commentCount ?? 0) > 0 ? <span>{email.commentCount}</span> : null}
                </span>
              )}
              {email.hasNote && (
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 flex-shrink-0" title="Has note">
                  <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </span>
              )}
              {email.isArchived && (
                <span className={cn('inline-flex items-center justify-center w-3.5 h-3.5 flex-shrink-0', colors.textMuted)} title="Archived">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </span>
              )}
              {(email.tags?.length ?? 0) > 0 && (
                <span className="flex items-center gap-0.5 min-w-0 flex-shrink max-w-[140px]" title={email.tags?.map((t) => t.name).join(', ')}>
                  {email.tags!.slice(0, 2).map((t) => (
                    <span
                      key={t.id}
                      className={cn('inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium truncate max-w-[72px]', !t.color && cn(colors.textMuted, 'bg-gray-100 dark:bg-gray-700'))}
                      style={t.color ? { backgroundColor: `${t.color}20`, color: t.color } : undefined}
                    >
                      {t.name}
                    </span>
                  ))}
                  {(email.tags?.length ?? 0) > 2 && (
                    <span className={cn('text-[10px]', colors.textMuted)}>+{(email.tags?.length ?? 0) - 2}</span>
                  )}
                </span>
              )}
              {hasLinkedClient && (
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 flex-shrink-0 text-indigo-600" title={clientTitle}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </span>
              )}
              {hasLinkedTodo && (
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 flex-shrink-0 text-teal-600" title={todoTitle}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </span>
              )}
              {hasLinkedAppointment && (
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 flex-shrink-0 text-sky-600" title={appointmentTitle}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

function propsAreEqual(prev: EmailListCardProps, next: EmailListCardProps): boolean {
  return (
    prev.email === next.email
    && prev.isSelected === next.isSelected
    && prev.isBulkSelected === next.isBulkSelected
    && prev.showMailboxLocation === next.showMailboxLocation
    && prev.showReadByCount === next.showReadByCount
    && prev.listSortBy === next.listSortBy
    && prev.colors === next.colors
    && prev.onEmailClick === next.onEmailClick
    && prev.onToggleRead === next.onToggleRead
    && prev.onBulkToggleSelect === next.onBulkToggleSelect
    && prev.cardRef === next.cardRef
  );
}

export default React.memo(EmailListCard, propsAreEqual);
